import React from "react";
import { CardBodySection } from "../../../components";
import { optionalClassName, prefixClassName } from "@smartrr/shared/components/primitives";
import { useSmartrrTheme } from "@customer-app/account/views/OperationAquarius/hooks/useSmartrrTheme";
import { Stack, Typography } from "@mui/material";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { IOrderSchedule, renderOrderAnchorDatesState, renderOrderFiniteState } from "../../../utils";
import { useOrganization } from "@customer-app/account/views/OperationAquarius/utils";
import { isCPSLineItemBundleParent, isOrderLineItemBundleItem } from "@smartrr/shared/utils/bundles";
import { OrderItemImage } from "../../../../Subscription/constants";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { applyDiscounts } from "@smartrr/shared/utils/getDiscounts";
import { ISmartrrOrderLineItem } from "@smartrr/shared/entities/OrderLineItem";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ISmartrrOrderFromDb } from "@smartrr/shared/entities/Order";
import { useCleanedAndSortedPurchables } from "@customer-app/state/reducers/purchasables";
import { IOrderLineItemDiscount } from "@smartrr/shared/interfaces/Discount";
import { RenderItemDetails } from "./OrderItemDetails";
import { OrderItemVariantName } from "./OrderItemVariantName/OrderItemVariantName";
import { OrderItemVariantDiscountPricing } from "./OrderItemVariantDiscountPricing/OrderItemVariantDiscountPricing";
import { OrderItemVariantUndiscountedPricing } from "./OrderItemVariantUndiscountedPricing/OrderItemVariantUndiscountedPricing";

export const OrderHistoryOrderItems = ({
  subscription,
  orderItems,
  index,
  order,
}: {
  index: number;
  orderItems: ISmartrrOrderLineItem[];
  subscription?: IPurchaseState;
  order: ISmartrrOrderFromDb;
}) => {
  const { orderCurrency } = order;
  const theme = useSmartrrTheme();
  const organization = useOrganization();
  const { purchasables } = useCleanedAndSortedPurchables();
  const hasBundle = subscription?.containsBundle || false;

  return (
    <React.Fragment>
      <CardBodySection
        spacing={2.5}
        borderColor={theme.modernLineDividers}
        className={optionalClassName({
          "order-has-bundle": hasBundle,
        })}
      >
        {subscription != null && (
          <Stack spacing={1}>
            <Typography variant="caption1" sx={{ opacity: "70%" }} className={prefixClassName("subscription-id")}>
              {`${typedLocalize("general.subscriptionNum", {
                num:
                  subscription.shopifyId && !subscription.shopifyId.includes("Order")
                    ? `${shopifyGidToNumber(subscription?.shopifyId || "")}`
                    : "",
              })}`}
            </Typography>
            {(subscription.schedule?.paymentFrequencyMultiple || 0) > 1 && order.orderCycleIndex != null && (
              <Typography variant="caption1" sx={{ opacity: "70%" }}>
                {typedLocalize("orderHistory.prepaidDelivery", {
                  firstValue: order.orderCycleIndex + 1,
                  secondValue: subscription.schedule.paymentFrequencyMultiple,
                })}
              </Typography>
            )}
            {renderOrderFiniteState(subscription, organization) && (
              <Typography variant="caption1" sx={{ opacity: "70%" }}>
                {renderOrderFiniteState(subscription, organization)}
              </Typography>
            )}
            {!!Object.keys((subscription.schedule as IOrderSchedule)?.anchor).length && (
              <Typography variant="caption1" sx={{ opacity: "70%" }}>
                {renderOrderAnchorDatesState((subscription.schedule as IOrderSchedule).anchor)}
              </Typography>
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{ overflowX: "auto", marginRight: "-20px !important" }}
          px="6px"
          pt="6px"
        >
          {orderItems.map(item => {
            const subscriptionItemFromItem = subscription?.stLineItems.find(
              st => st.vnt.shopifyId === item.vnt?.shopifyId
            );
            const appliedDiscounts =
              subscription && subscriptionItemFromItem
                ? subscriptionItemFromItem.discounts.filter(discount => !discount.rejectionReason)
                : item.discountAllocations.filter(discount => !discount.rejectionReason);

            const lineItemType = {
              bundleMain: false,
              bundleLineItem: isOrderLineItemBundleItem(item),
              redeemed: false,
              addOn: false,
            };

            if (subscriptionItemFromItem) {
              lineItemType.bundleMain = isCPSLineItemBundleParent(subscriptionItemFromItem);
              lineItemType.redeemed = subscriptionItemFromItem.isRedeemed || false;
              lineItemType.addOn = subscriptionItemFromItem.isAddOn;
            }

            return (
              <Stack
                data-smartrr-item-id={item.shopifyId}
                key={item.id}
                sx={{ maxWidth: "90px" }}
                className={optionalClassName({
                  "bundle-main-item": lineItemType.bundleMain,
                  "bundle-line-item": lineItemType.bundleLineItem,
                  "addon-line-item": lineItemType.addOn,
                  "redeemed-line-item": lineItemType.redeemed,
                })}
              >
                <OrderItemImage purchasableVariant={item.vnt} quantity={item.quantity} size="medium" />
                <Stack spacing="5px" mt={"7px"}>
                  <OrderItemVariantName item={item} purchasables={purchasables} />
                  <Stack spacing={0.3}>
                    {subscription && subscriptionItemFromItem ? (
                      <React.Fragment>
                        {appliedDiscounts.length ? (
                          <OrderItemVariantDiscountPricing
                            appliedDiscounts={appliedDiscounts}
                            item={item}
                            subscriptionItemFromItem={subscriptionItemFromItem}
                            orderCurrency={orderCurrency}
                          />
                        ) : (
                          <OrderItemVariantUndiscountedPricing
                            item={item}
                            subscriptionItemFromItem={subscriptionItemFromItem}
                            orderCurrency={orderCurrency}
                          />
                        )}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        {appliedDiscounts.length ? (
                          appliedDiscounts.map((_, i: number) => {
                            const endingPrice = formatMoney(
                              applyDiscounts(
                                item.price,
                                appliedDiscounts.slice(0, i + 1) as IOrderLineItemDiscount[],
                                0,
                                item.quantity
                              ),
                              orderCurrency
                            );
                            return (
                              <React.Fragment key={i}>
                                <Stack
                                  direction="row"
                                  sx={{ marginBottom: "3px", flexWrap: "wrap" }}
                                  className={prefixClassName("order-applied-discount-pricing")}
                                >
                                  <Typography
                                    variant="caption1"
                                    sx={{
                                      textDecoration: i < appliedDiscounts.length - 1 ? "line-through" : "none",
                                      marginRight: "3px",
                                    }}
                                    className={prefixClassName("order-product-ending-price")}
                                  >
                                    {endingPrice}
                                  </Typography>
                                  <Typography
                                    variant="caption1"
                                    sx={{
                                      textDecoration: appliedDiscounts.length ? "line-through" : "none",
                                    }}
                                    className={prefixClassName("order-product-base-price")}
                                  >
                                    {formatMoney(item.price, orderCurrency)}
                                  </Typography>
                                </Stack>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <Typography
                            variant="caption1"
                            className={prefixClassName("order-undiscounted-pricing")}
                          >
                            {formatMoney(item.price, orderCurrency)}
                          </Typography>
                        )}
                      </React.Fragment>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </CardBodySection>
      <RenderItemDetails order={order} subscription={subscription} index={index} />
    </React.Fragment>
  );
};
